import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { currencyMeta, formatHuman, fromMinor } from '@fint/money';
import { cn } from '@/lib/cn';
import { colourSolid } from '../Classification';
import { compactAmount } from './layout';

/**
 * What every chart in the kit is built on.
 *
 * ── HAND-ROLLED SVG, AND WHY ──
 * The app loads nothing from another origin (see index.html), so a chart
 * library would have to be bundled; the small ones still cost more than the
 * four charts here, and none of them draws with the theme's tokens or the
 * classification palette without a wrapper that would be most of this file
 * anyway. An SVG element with a viewBox is responsive by construction, prints,
 * and takes `hsl(var(--border))` for a gridline like any other element.
 *
 * ── THREE READERS, THREE LAYERS ──
 * A person with a mouse gets the picture, a `<title>` on every mark, and a
 * tooltip that follows the pointer. A person with a keyboard gets the same
 * tooltip by stepping through the months with the arrow keys, read out
 * through a live region. A screen reader gets the summary sentence on the
 * `role="img"` and, under it, a visually-hidden table of every number the
 * picture encodes - which is also what the end-to-end tests read, so the
 * numbers on screen and the numbers a test asserts are the same cells.
 *
 * ── MONEY ──
 * Every amount arrives as an integer of minor units, parsed by `@fint/money`
 * in the page. The kit divides by the currency's exponent for a tick label
 * and otherwise never does arithmetic on money; `moneyText` renders through
 * `formatHuman`, so 5,00,000 is grouped the way the reader counts. No symbol
 * inside a chart: the caption names the currency once, and a symbol on every
 * tick, legend row and table cell is noise a screen reader reads aloud.
 */

/** The theme's ink for chart chrome. Tokens, never colours: both themes and print read from the same rules. */
export const INK = {
  grid: 'hsl(var(--border))',
  zero: 'hsl(var(--muted-foreground) / 0.55)',
  axis: 'hsl(var(--muted-foreground))',
  text: 'hsl(var(--foreground))',
  surface: 'hsl(var(--card))',
  /** The one series colour that is not a book or a category: the combined line. */
  primary: 'hsl(var(--primary))',
  muted: 'hsl(var(--muted))',
} as const;

/** An amount, grouped for the reader, without a symbol. A negative one carries U+2212, as `Signed` does, not a hyphen. */
export function moneyText(minor: number, currency: string): string {
  const text = formatHuman(fromMinor(Math.abs(minor), currency), { showSymbol: false });
  return minor < 0 ? `−${text}` : text;
}

/** A signed amount for a tooltip: a plus as well as the minus. */
export function signedText(minor: number, currency: string): string {
  const text = moneyText(minor, currency);
  return minor > 0 ? `+${text}` : text;
}

/**
 * One window against the one before it: the amount, then how big a move it was.
 *
 * Every chart that carries a "vs last month" row says it this way, so the
 * reader learns the shape once - it began on the Reports months chart and is
 * here because the Overview's charts now answer the same question. A baseline
 * of nothing has no percentage: dividing by it gives an infinity, and "new" is
 * what a person would have said anyway.
 */
export function changeText(now: number, before: number, currency: string): string {
  const delta = now - before;
  if (before === 0) return delta === 0 ? 'nothing either time' : `${signedText(delta, currency)} · new`;
  const percent = Math.round((delta / Math.abs(before)) * 100);
  return `${signedText(delta, currency)} · ${delta > 0 ? '+' : delta < 0 ? '−' : ''}${Math.abs(percent)}%`;
}

/** An axis tick: 1.5 L, 20K, 2 Cr. Indian grouping when the currency is Indian. */
export function tickText(minor: number, currency: string): string {
  const meta = currencyMeta(currency);
  return compactAmount(minor / 10 ** meta.exponent, meta.locale === 'en-IN');
}

// ── measuring ─────────────────────────────────────────────────────────────

/**
 * The container's width in CSS pixels, kept current.
 *
 * A viewBox alone would scale the whole picture, text included, which is
 * illegible on a phone and comically large on a wide screen. Measuring the
 * container and laying the chart out in real pixels keeps every label the
 * size the rest of the page uses. The first render uses the fallback, which
 * is replaced before paint.
 *
 * A callback ref rather than an effect, because the element comes and goes:
 * a chart renders its empty state for one period and its frame for the
 * next, and an effect that ran once at mount would never see the frame.
 */
export function useMeasuredWidth<T extends HTMLElement>(fallback = 640): [(element: T | null) => void, number] {
  const [width, setWidth] = useState(fallback);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((element: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!element) return;
    const measure = () => {
      const next = Math.round(element.getBoundingClientRect().width);
      if (next > 0) setWidth((current) => (current === next ? current : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(measure);
    observer.current.observe(element);
  }, []);
  return [ref, width];
}

/** Whether the person has asked for less motion. Read once and kept current. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() =>
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)').matches : true,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

// ── the tooltip ───────────────────────────────────────────────────────────

export type SwatchKind = 'rect' | 'tint' | 'hatch' | 'line' | 'dot';

export interface TooltipRow {
  key: string;
  /** A palette token, or null for the theme's primary; undefined draws no swatch. */
  colour?: string | null;
  kind?: SwatchKind;
  label: string;
  value: string;
}

export interface TooltipState {
  /** Where to point, in the frame's own pixels. */
  x: number;
  y: number;
  title: string;
  rows: TooltipRow[];
}

/**
 * The colour a series draws in: a palette token; null for the theme's
 * primary (the combined series); or `foreground` for the theme's ink, which
 * is what a line drawn OVER coloured bars needs to be seen against all of
 * them. Anything else falls through `resolveColour`'s fail-soft to slate.
 */
export function seriesColour(colour: string | null | undefined): string {
  if (colour === 'foreground') return INK.text;
  return colour ? colourSolid(colour) : INK.primary;
}

/**
 * A small key beside a label: the mark's own shape, so a legend for bars
 * shows a block and a legend for a line shows a stroke. Hatching is the
 * kit's second channel for "money out" - texture rather than a second
 * colour, so a book keeps its one colour on both bars and the difference
 * survives a greyscale print.
 */
export function Swatch({ colour, kind = 'rect' }: { colour: string | null | undefined; kind?: SwatchKind }) {
  const fill = seriesColour(colour);
  if (kind === 'line') {
    return <span aria-hidden className="inline-block h-0.5 w-3 shrink-0 rounded-full" style={{ backgroundColor: fill }} />;
  }
  if (kind === 'dot') {
    return <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: fill }} />;
  }
  if (kind === 'tint') {
    return <span aria-hidden className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: fill, opacity: 0.4 }} />;
  }
  if (kind === 'hatch') {
    return (
      <svg aria-hidden className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" viewBox="0 0 10 10">
        <rect width="10" height="10" fill={fill} opacity="0.25" />
        <path d="M-2 4 L4 -2 M0 10 L10 0 M6 12 L12 6" stroke={fill} strokeWidth="1.6" />
      </svg>
    );
  }
  return <span aria-hidden className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: fill }} />;
}

/** The hatch a chart paints "money out" with. Mounted once per chart in its `<defs>`; one pattern per colour. */
export function HatchPattern({ id, colour }: { id: string; colour: string | null | undefined }) {
  const ink = seriesColour(colour);
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
      <rect width="6" height="6" fill={ink} opacity="0.22" />
      <line x1="0" y1="0" x2="0" y2="6" stroke={ink} strokeWidth="2.2" />
    </pattern>
  );
}

/** A flat row of keys. Spans, not list items: the sr-only table is the structure a reader navigates. */
export function Legend({ items, className }: { items: Array<{ key: string; label: string; colour?: string | null; kind?: SwatchKind }>; className?: string }) {
  if (items.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground', className)} data-chart-legend>
      {items.map((item) => (
        <span key={item.key} className="inline-flex min-w-0 items-center gap-1.5">
          <Swatch colour={item.colour} kind={item.kind} />
          <span className="truncate">{item.label}</span>
        </span>
      ))}
    </div>
  );
}

// ── the frame ─────────────────────────────────────────────────────────────

export interface ChartTable {
  caption: string;
  columns: string[];
  rows: Array<{ key: string; cells: string[] }>;
}

/**
 * The wrapper every chart sits in: the picture, its tooltip, and its table.
 *
 * The chart puts its one-sentence summary on the `role="img"` SVG it
 * passes as children; `table` is the same data as cells. `tooltip` is drawn by the frame so the
 * charts only decide WHAT to say and WHERE; the frame clamps it inside its
 * own width so a month at the right edge never pushes the page wider - the
 * device sweep measures that.
 *
 * `onStep` makes the frame a keyboard target: the arrow keys call it with
 * -1/+1 (Home and End with the ends), and whatever the chart then puts in
 * the tooltip is also announced through the live region.
 */
export function ChartFrame({
  title,
  table,
  tooltip,
  onStep,
  onLeave,
  onSelect,
  frameRef,
  children,
  className,
}: {
  /** The chart's name, for the keyboard group. */
  title: string;
  table: ChartTable;
  tooltip: TooltipState | null;
  onStep?: (direction: -1 | 1 | 'first' | 'last') => void;
  onLeave?: () => void;
  /** Enter on the stepped-to month: the chart's click, from the keyboard. */
  onSelect?: () => void;
  frameRef?: Ref<HTMLDivElement>;
  children: ReactNode;
  className?: string;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onStep) return;
    const map: Record<string, -1 | 1 | 'first' | 'last'> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: 'first',
      End: 'last',
    };
    const step = map[event.key];
    if (step !== undefined) {
      event.preventDefault();
      onStep(step);
    } else if (event.key === 'Escape') {
      onLeave?.();
    } else if ((event.key === 'Enter' || event.key === ' ') && onSelect) {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      ref={frameRef}
      className={cn('relative w-full', onStep && 'rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
      role={onStep ? 'group' : undefined}
      aria-label={onStep ? `${title}. Use the arrow keys to step through the months${onSelect ? ', Enter to open one' : ''}.` : undefined}
      tabIndex={onStep ? 0 : undefined}
      onKeyDown={onKeyDown}
      onBlur={onStep ? onLeave : undefined}
      data-chart={title}
    >
      {children}
      {tooltip ? <Tooltip state={tooltip} /> : null}
      {onStep ? (
        <span className="sr-only" aria-live="polite">
          {tooltip ? `${tooltip.title}: ${tooltip.rows.map((row) => `${row.label} ${row.value}`).join(', ')}` : ''}
        </span>
      ) : null}
      {/* The table sits INSIDE a visually-hidden div rather than being one
          itself. `sr-only` sets width to 1px, and a table treats width as a
          minimum: with `white-space: nowrap` a row of amounts laid the table
          out a thousand pixels wide, clipped from view but still counted in
          the page's scrollable width - and a phone scrolled sideways into
          nothing. A div honours the 1px and clips what is inside it. */}
      <div className="sr-only">
        <table>
          <caption>{table.caption}</caption>
          <thead>
            <tr>
              {table.columns.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, index) =>
                  index === 0 ? (
                    <th key={index} scope="row">
                      {cell}
                    </th>
                  ) : (
                    <td key={index}>{cell}</td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The floating readout. Values lead and labels follow, because the reader
 * has already picked the month and wants the number. Positioned above the
 * point and centred on it, then clamped to the frame.
 */
function Tooltip({ state }: { state: TooltipState }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    const frame = element?.parentElement;
    if (!element || !frame) return;
    const half = element.offsetWidth / 2;
    const width = frame.clientWidth;
    let next = 0;
    if (state.x - half < 0) next = half - state.x;
    else if (state.x + half > width) next = width - state.x - half;
    setShift(Math.round(next));
  }, [state.x, state.rows.length]);

  return (
    <div
      ref={ref}
      role="presentation"
      className="pointer-events-none absolute z-10 w-max max-w-[300px] rounded-md border border-border bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md"
      style={{
        left: state.x + shift,
        top: state.y,
        transform: 'translate(-50%, calc(-100% - 10px))',
      }}
      data-chart-tooltip
    >
      <p className="mb-1 font-medium">{state.title}</p>
      <dl className="space-y-0.5">
        {state.rows.map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-3">
            <dt className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              {row.colour !== undefined ? <Swatch colour={row.colour} kind={row.kind ?? 'line'} /> : null}
              <span className="truncate">{row.label}</span>
            </dt>
            <dd className="shrink-0 font-medium tabular-nums">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ── the empty state ───────────────────────────────────────────────────────

/**
 * What a chart shows for a period with nothing in it: a sentence, not an
 * axis. An empty axis with a zero tick looks like a chart that failed to
 * load; "nothing moved" is the fact.
 */
export function EmptyChart({ title, description }: { title: string; description?: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border px-4 py-10 text-center"
      data-chart-empty
    >
      <p className="text-sm font-medium">{title}</p>
      {description ? <p className="max-w-sm text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}

// ── shared axis pieces ────────────────────────────────────────────────────

/** Horizontal gridlines with tick labels down the left. Hairlines, recessive; zero a shade darker. */
export function YAxis({
  ticks,
  y,
  left,
  right,
  currency,
}: {
  ticks: number[];
  y: (value: number) => number;
  left: number;
  right: number;
  currency: string;
}) {
  return (
    <g aria-hidden>
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={left}
            x2={right}
            y1={y(tick)}
            y2={y(tick)}
            stroke={tick === 0 ? INK.zero : INK.grid}
            strokeWidth={1}
            shapeRendering="crispEdges"
          />
          <text x={left - 6} y={y(tick)} dy="0.35em" textAnchor="end" fontSize={11} fill={INK.axis} className="tabular-nums">
            {tickText(tick, currency)}
          </text>
        </g>
      ))}
    </g>
  );
}

/**
 * The width the tick column needs: the longest tick label, in characters,
 * at roughly 6.5px each for 11px figures, plus the gap to the plot. Measured
 * rather than fixed, because "3 L" on a phone and "−12.5 L" on a laptop
 * are not the same column, and a fixed 56px was a quarter of a 390px
 * screen spent on three characters.
 */
export function tickColumnWidth(ticks: number[], currency: string): number {
  const longest = Math.max(1, ...ticks.map((tick) => tickText(tick, currency).length));
  return Math.round(longest * 6.5) + 10;
}

/**
 * The month labels along the bottom, thinned so they never touch.
 *
 * How many to skip comes from the labels' own width against the slot
 * width - "Aug 26" is six characters at 11px, about 40px, and needs that
 * plus a gap - rather than from fixed thresholds, which put "Jun 26" and
 * "Aug 26" a few pixels apart on a phone showing a year. The first month
 * is always labelled; the ones kept after it are every Nth.
 */
export function XAxis({
  labels,
  centre,
  y,
  band,
}: {
  labels: string[];
  centre: (i: number) => number;
  y: number;
  /** The distance from one slot to the next, in px. */
  band: number;
}) {
  const longest = Math.max(1, ...labels.map((label) => label.length));
  const needed = longest * 6.2 + 12;
  const every = band > 0 ? Math.max(1, Math.ceil(needed / band)) : 1;
  return (
    <g aria-hidden>
      {labels.map((label, i) =>
        i % every === 0 ? (
          <text key={label + i} x={centre(i)} y={y} textAnchor="middle" fontSize={11} fill={INK.axis}>
            {label}
          </text>
        ) : null,
      )}
    </g>
  );
}

/** Keyframes the marks may use. Skipped entirely under reduced motion, so a still page stays still. */
export function MotionStyles({ enabled }: { enabled: boolean }) {
  if (!enabled) return null;
  return (
    <style>{`@keyframes chart-grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes chart-grow-x { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes chart-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
@keyframes chart-fade { from { opacity: 0; } to { opacity: 1; } }`}</style>
  );
}
